import ContactActionsController from "./contact_actions_controller"
import { csrfToken } from "../utils/csrf"

const DEMO_GROUP_STORAGE_KEY = "demo_contact_groups"

export default class extends ContactActionsController {
    static values = { membershipBase: String, removeGroupSlug: String, removeGroupName: String }
    static targets = ["caret", "menu", "groupSection", "groupCaret", "groupList"]

    connect() {
        super.connect()
        this._menuObserver = new MutationObserver(() => {
            if (!this.menuTarget.classList.contains("is-open")) {
                this._clearPanelPosition()
                this._collapseGroups()
            }
        })
        this._menuObserver.observe(this.menuTarget, { attributes: true, attributeFilter: ["class"] })
    }

    disconnect() {
        super.disconnect()
        this._menuObserver?.disconnect()
        this._clearPanelPosition()
    }

    stopPropagation(e) {
        e.stopPropagation()
    }

    toggleMenu(e) {
        e.preventDefault()
        e.stopPropagation()
        let splitMenu = this.application.getControllerForElementAndIdentifier(this.element, "split-menu")
        if (!splitMenu) return

        if (this.menuTarget.classList.contains("is-open")) {
            splitMenu.close()
            this._clearPanelPosition()
            this._collapseGroups()
        } else {
            splitMenu.open()
            this._positionPanel()
        }
    }

    toggleGroups(e) {
        e.preventDefault()
        e.stopPropagation()
        if (!this.hasGroupSectionTarget) return
        this.groupSectionTarget.classList.toggle("is-expanded")
        if (this.hasGroupCaretTarget) {
            this.groupCaretTarget.classList.toggle("is-open", this.groupSectionTarget.classList.contains("is-expanded"))
        }
    }

    toggleGroup(e) {
        e.preventDefault()
        e.stopPropagation()
        let slug = e.currentTarget.dataset.slug
        if (!slug) return

        if (this.demoValue) {
            this._toggleDemoGroup(slug, e.currentTarget)
            return
        }

        let member = this.memberSlugsValue.includes(slug)
        this._requestMembership(slug, member ? "DELETE" : "POST")
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update group")
                this._setMember(slug, data.member)
                this._syncGroupRow(e.currentTarget, data.member)
            })
            .catch(() => {})
    }

    removeFromCurrentGroup(e) {
        e.preventDefault()
        e.stopPropagation()
        let slug = this.removeGroupSlugValue
        if (!slug) return

        if (this.demoValue) {
            this._toggleDemoGroup(slug, null, false)
            document.dispatchEvent(new CustomEvent("demo-contact-groups:changed", { detail: { contactId: this.contactIdValue, slugs: this.memberSlugsValue } }))
            return
        }

        this._requestMembership(slug, "DELETE")
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not update group")
                this._setMember(slug, false)
            })
            .catch(() => {})
    }

    _syncGroupRow(row, member) {
        if (!row) return
        row.classList.toggle("is-active", member)
        let check = row.querySelector(".contact-row-menu-check")
        if (member && !check) {
            row.insertAdjacentHTML("beforeend", '<i class="bi bi-check-lg contact-row-menu-check" aria-hidden="true"></i>')
        } else if (!member && check) {
            check.remove()
        }
    }

    _setMember(slug, member) {
        let slugs = new Set(this.memberSlugsValue)
        if (member) slugs.add(slug)
        else slugs.delete(slug)
        this.memberSlugsValue = [...slugs]
    }

    _requestMembership(slug, method) {
        let url = `${this.membershipBaseValue}${encodeURIComponent(slug)}`
        return fetch(url, {
            method,
            headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin"
        }).then((response) => response.json().then((data) => ({ response, data })))
    }

    _toggleDemoGroup(slug, row, member = null) {
        let nextMember = member
        if (nextMember === null) nextMember = !this.memberSlugsValue.includes(slug)
        let state = this._loadDemoGroupState()
        let slugs = new Set(state.memberships[this.contactIdValue] || [])
        if (nextMember) slugs.add(slug)
        else slugs.delete(slug)
        state.memberships[this.contactIdValue] = [...slugs]
        this._saveDemoGroupState(state)
        this._setMember(slug, nextMember)
        if (row) this._syncGroupRow(row, nextMember)
        document.dispatchEvent(new CustomEvent("demo-contact-groups:changed", { detail: { contactId: this.contactIdValue, slugs: [...slugs] } }))
    }

    _positionPanel() {
        if (!this.hasMenuTarget || !this.hasCaretTarget) return
        let rect = this.caretTarget.getBoundingClientRect()
        let panel = this.menuTarget
        panel.style.position = "fixed"
        panel.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`
        panel.style.left = "auto"
        panel.style.minWidth = "min(17rem, calc(100vw - 16px))"
        panel.style.zIndex = "1200"

        let maxHeight = Math.max(160, window.innerHeight - 32)
        panel.style.maxHeight = `${maxHeight}px`
        panel.style.overflowY = "auto"

        let panelHeight = panel.offsetHeight || maxHeight
        let top = rect.bottom + 6
        if (top + panelHeight > window.innerHeight - 8) {
            top = Math.max(8, rect.top - panelHeight - 6)
        }
        panel.style.top = `${top}px`
    }

    _clearPanelPosition() {
        if (!this.hasMenuTarget) return
        let panel = this.menuTarget
        panel.style.position = ""
        panel.style.top = ""
        panel.style.right = ""
        panel.style.left = ""
        panel.style.minWidth = ""
        panel.style.maxHeight = ""
        panel.style.overflowY = ""
        panel.style.zIndex = ""
    }

    _collapseGroups() {
        if (!this.hasGroupSectionTarget) return
        this.groupSectionTarget.classList.remove("is-expanded")
        if (this.hasGroupCaretTarget) this.groupCaretTarget.classList.remove("is-open")
    }

    _loadDemoGroupState() {
        try {
            let raw = sessionStorage.getItem(DEMO_GROUP_STORAGE_KEY)
            let parsed = raw ? JSON.parse(raw) : {}
            return {
                memberships: parsed.memberships || {},
                custom_groups: parsed.custom_groups || []
            }
        } catch {
            return { memberships: {}, custom_groups: [] }
        }
    }

    _saveDemoGroupState(state) {
        try {
            sessionStorage.setItem(DEMO_GROUP_STORAGE_KEY, JSON.stringify(state))
        } catch {
            // sessionStorage unavailable — ignore
        }
    }
}
